// Continuous-broadcast loop decisions (PLAN §"Racing V2 interface" →
// "Continuous broadcast"). The pacing and shape mirror the donor table loop
// (og-v2-frontend multiplayer.js: betting → locked → spinning → settling →
// intermission → betting, one long-lived scene; main.js advanceToNextCard):
// the official result holds, then ONE canonical transition re-fields the
// same scene into the next round. HRW's rounds live on a world schedule, so
// the "next round" is the next scheduled race and the swap is gated on its
// racecard already being local — never a teardown, never a page navigation.

/** How long an official result holds on screen before the broadcast flows
 * to the next round (founder: 10–15s; the next betting window is already
 * open by then because the schedule overlaps windows). */
export const RESULT_HOLD_MS = 12_000;

/** Who asked for the transition: the intermission scheduler or the player's
 * explicit Continue. Both take the same gated path. */
export type PendingAdvance = "auto" | "manual" | null;

export type LoopRaceSummary = {
  raceId: string;
  status: string;
};

/** The round the broadcast flows to next: the first race after the viewed
 * one in schedule order that has not gone official. When the viewed race has
 * already left the schedule (the normal case once its result is official)
 * that is simply the earliest non-official race. Never the viewed race
 * itself and never an official result — advancing into a settled race would
 * strand the loop on a result screen that (by design) never auto-chains. */
export function pickNextRace(
  races: readonly LoopRaceSummary[],
  currentRaceId: string | null,
): string | null {
  const index = races.findIndex((race) => race.raceId === currentRaceId);
  const next = races.find(
    (race, position) =>
      position > index &&
      race.raceId !== currentRaceId &&
      race.status !== "official",
  );
  return next?.raceId ?? null;
}

/** Result-footer copy for the intermission: the visible countdown while the
 * official result holds, then the readiness/waiting states while the swap
 * is gated on the next racecard (schedule gaps included). Empty when the
 * loop is not advancing. The hold copy names the ADVANCE ("Continuing in
 * Ns" — when this board flows to the next betting window, the same event
 * the Continue button triggers early); the fullscreen capsule's
 * "NEXT RACE IN m:ss" counts to the next race's actual start, and the two
 * must never carry the same label with different numbers. */
export function advanceCopy({
  advanceAt,
  nowMs,
  pending,
  nextRaceKnown,
  nextRaceReady,
}: {
  advanceAt: number | null;
  nowMs: number;
  pending: PendingAdvance;
  nextRaceKnown: boolean;
  nextRaceReady: boolean;
}): string {
  if (advanceAt !== null) {
    const seconds = Math.max(0, Math.ceil((advanceAt - nowMs) / 1000));
    return `Continuing in ${seconds}s`;
  }
  if (!pending) return "";
  if (!nextRaceKnown) return "Waiting for the next race…";
  if (!nextRaceReady) return "Next race loading…";
  return "Next race ready";
}
